import { Common, Platform } from '@discord/embedded-app-sdk';
import type { DiscordSDK } from '@discord/embedded-app-sdk';

/**
 * Adaptation a la plateforme hote (desktop, web, iOS, Android).
 *
 * Rien ici n'est bloquant : chaque commande RPC est enveloppee, une commande
 * absente d'une plateforme ne doit jamais empecher l'activite de demarrer.
 */

export type HostPlatform = 'desktop' | 'mobile' | 'unknown';
export type ScreenOrientation = 'portrait' | 'landscape';

/** Etats de verrouillage d'orientation exposes par le SDK 2.5.0. */
const LOCK = Common.OrientationLockStateTypeObject;
export type OrientationLock = (typeof LOCK)[keyof typeof LOCK];

export function hostPlatform(sdk: DiscordSDK | null): HostPlatform {
  if (sdk === null) return 'unknown';
  if (sdk.platform === Platform.MOBILE) return 'mobile';
  if (sdk.platform === Platform.DESKTOP) return 'desktop';
  return 'unknown';
}

/**
 * `setOrientationLockState` n'existe que sur mobile : sur desktop la commande
 * repond en erreur. On la tente et on ignore l'echec — d'ou le try/catch, ce
 * n'est pas de la paresse mais le comportement documente.
 *
 * On laisse volontairement l'orientation DEVERROUILLEE : les deux mises en page
 * (portrait et paysage) sont de vraies mises en page, pas un zoom.
 */
export async function setOrientationLock(
  sdk: DiscordSDK | null,
  lockState: OrientationLock = LOCK.UNLOCKED,
): Promise<boolean> {
  if (sdk === null) return false;
  try {
    await sdk.commands.setOrientationLockState({
      lock_state: lockState,
      picture_in_picture_lock_state: LOCK.LANDSCAPE,
      grid_lock_state: LOCK.UNLOCKED,
    });
    return true;
  } catch {
    return false;
  }
}

export function currentOrientation(): ScreenOrientation {
  return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
}

/**
 * Pose sur `<html>` les attributs que le CSS consomme :
 *  - data-platform : cibles tactiles et marges specifiques mobile
 *  - data-orientation : bascule entre les deux mises en page
 * Les safe areas viennent de `env(safe-area-inset-*)` cote CSS (avec
 * `viewport-fit=cover` dans index.html) ; on n'essaie pas de les mesurer en JS,
 * les valeurs changent a la rotation et au clavier virtuel.
 */
export function installPlatformAttributes(sdk: DiscordSDK | null): () => void {
  const root = document.documentElement;
  root.dataset['platform'] = hostPlatform(sdk);

  const portrait = window.matchMedia('(orientation: portrait)');
  const sync = (): void => {
    root.dataset['orientation'] = portrait.matches ? 'portrait' : 'landscape';
  };
  sync();
  portrait.addEventListener('change', sync);

  const coarse = window.matchMedia('(pointer: coarse)');
  const syncPointer = (): void => {
    root.dataset['pointer'] = coarse.matches ? 'coarse' : 'fine';
  };
  syncPointer();
  coarse.addEventListener('change', syncPointer);

  return () => {
    portrait.removeEventListener('change', sync);
    coarse.removeEventListener('change', syncPointer);
  };
}
